import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";

const PolicyAlterations = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchData, setSearchData] = useState({ customerName: "", policyNumber: "" });
  const [policyData, setPolicyData] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [originalData, setOriginalData] = useState(null);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [customerShowSuggestions, setCustomerShowSuggestions] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const { user } = useAuth();

  // ===== VALIDATION STATE & HELPERS =====
  const [errors, setErrors] = useState({
    search: {},
    fields: {}, // for editable form fields
  });

  const setFieldError = (section, field, message) => {
    setErrors(prev => ({ ...prev, [section]: { ...prev[section], [field]: message } }));
  };
  const clearFieldError = (section, field) => {
    setErrors(prev => {
      const copy = { ...prev[section] };
      delete copy[field];
      return { ...prev, [section]: copy };
    });
  };
  const getInputErrorStyle = (section, field) =>
    errors?.[section]?.[field]
      ? { borderColor: "crimson", boxShadow: "0 0 0 2px rgba(220,20,60,.08)" }
      : {};
  const errorText = { color: "crimson", fontSize: 12, marginTop: 6 };

  // Patterns
  const policyNoPattern = /^[A-Za-z0-9\-_/]{3,40}$/;
  const phonePattern = /^[6-9]\d{9}$/; // Indian 10-digit starting 6-9
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const moneyPattern = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;
  const allowedPaymentModes = new Set(["Monthly", "Quarterly", "Yearly"]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ===== SEARCH VALIDATION + HANDLER =====
  const validateSearch = () => {
    let ok = true;
    if (!searchData.policyNumber.trim() || !policyNoPattern.test(searchData.policyNumber.trim())) {
      setFieldError("search", "policyNumber", "Enter a valid policy number (3–40 chars, A–Z 0–9 - _ /).");
      ok = false;
    } else {
      clearFieldError("search", "policyNumber");
    }
    // Customer name is optional in your flow; if provided, sanity check
    if (searchData.customerName.trim() && searchData.customerName.trim().length < 2) {
      setFieldError("search", "customerName", "Name should be at least 2 characters.");
      ok = false;
    } else {
      clearFieldError("search", "customerName");
    }
    return ok;
  };

  const handleSearch = async () => {
    if (!validateSearch()) {
      alert("Please correct highlighted fields.");
      return;
    }
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/policies/search?policyNumber=${searchData.policyNumber.trim()}`
      );
      const data = await res.json();
      if (res.ok) {
        setPolicyData(data);
        setOriginalData(data);
        setShowInstructions(false);
      } else {
        alert(data.msg || "Policy not found");
      }
    } catch (error) {
      console.error("Error fetching policy:", error);
      alert("Failed to fetch policy");
    }
  };

  // ===== LIVE FIELD CHANGE + PER-FIELD VALIDATION =====
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["premium", "startDate", "maturityDate", "paymentMode"].includes(name)) {
      setPolicyData((prev) => ({
        ...prev,
        policyDetails: { ...prev.policyDetails, [name]: value },
      }));
    } else {
      setPolicyData((prev) => ({ ...prev, [name]: value }));
    }

    // Live validation
    if (name === "customerPhone") {
      value ? (phonePattern.test(value.trim())
        ? clearFieldError("fields", "customerPhone")
        : setFieldError("fields", "customerPhone", "Enter a valid 10-digit mobile (starts 6–9)."))
        : clearFieldError("fields", "customerPhone");
    }
    if (name === "customerEmail") {
      value ? (emailPattern.test(value.trim())
        ? clearFieldError("fields", "customerEmail")
        : setFieldError("fields", "customerEmail", "Enter a valid email."))
        : clearFieldError("fields", "customerEmail");
    }
    if (name === "premium") {
      value ? (moneyPattern.test(String(value)) && Number(value) >= 0
        ? clearFieldError("fields", "premium")
        : setFieldError("fields", "premium", "Enter a valid amount (e.g., 500 or 500.50)."))
        : clearFieldError("fields", "premium");
    }
    if (name === "paymentMode") {
      value ? (allowedPaymentModes.has(value)
        ? clearFieldError("fields", "paymentMode")
        : setFieldError("fields", "paymentMode", "Choose Monthly, Quarterly, or Yearly."))
        : clearFieldError("fields", "paymentMode");
    }
    if (name === "startDate") {
      clearFieldError("fields", "startDate");
      // if maturity is present, validate ordering
      const start = new Date(value);
      const endVal = (policyData?.policyDetails?.endDate ?? policyData?.policyDetails?.maturityDate) || "";
      if (endVal) {
        const end = new Date(endVal);
        if (String(endVal) && !isNaN(end) && !isNaN(start) && end <= start) {
          setFieldError("fields", "maturityDate", "Maturity must be after start date.");
        } else {
          clearFieldError("fields", "maturityDate");
        }
      }
    }
    if (name === "maturityDate") {
      clearFieldError("fields", "maturityDate"); // ordering is rechecked on submit too
      const startVal = policyData?.policyDetails?.startDate || "";
      if (startVal && value) {
        const s = new Date(startVal);
        const m = new Date(value);
        if (!isNaN(s) && !isNaN(m) && m <= s) {
          setFieldError("fields", "maturityDate", "Maturity must be after start date.");
        }
      }
    }
    if (name === "company") {
      value.trim() ? clearFieldError("fields", "company") : setFieldError("fields", "company", "Company is required.");
    }
    if (name === "insuranceType") {
      value ? clearFieldError("fields", "insuranceType") : setFieldError("fields", "insuranceType", "Select a category.");
    }
    if (name === "policyType") {
      value ? clearFieldError("fields", "policyType") : setFieldError("fields", "policyType", "Select a type.");
    }
  };

  // ===== SUBMIT VALIDATION (BEFORE PATCH) =====
  const validateBeforeUpdate = () => {
    let ok = true;

    // Grab effective values from policyData
    const phone = policyData?.customerPhone ?? "";
    const email = policyData?.customerEmail ?? "";
    const premium = policyData?.policyDetails?.premium ?? "";
    const paymentMode = policyData?.policyDetails?.paymentMode ?? policyData?.paymentMode ?? "";
    const startDate = policyData?.policyDetails?.startDate ?? "";
    // API stores end as "endDate", input name uses "maturityDate"
    const maturityDate = policyData?.policyDetails?.endDate ?? policyData?.policyDetails?.maturityDate ?? "";

    const company = policyData?.company ?? "";
    const insuranceType = policyData?.insuranceType ?? "";
    const policyType = policyData?.policyType ?? "";

    // Phone (optional but if present, must be valid)
    if (phone && !phonePattern.test(String(phone).trim())) {
      setFieldError("fields", "customerPhone", "Enter a valid 10-digit mobile (starts 6–9).");
      ok = false;
    } else {
      clearFieldError("fields", "customerPhone");
    }

    // Email (optional but if present, must be valid)
    if (email && !emailPattern.test(String(email).trim())) {
      setFieldError("fields", "customerEmail", "Enter a valid email.");
      ok = false;
    } else {
      clearFieldError("fields", "customerEmail");
    }

    // Premium (optional but if present, must be valid non-negative number with 2 decimals max)
    if (premium !== "" && !(moneyPattern.test(String(premium)) && Number(premium) >= 0)) {
      setFieldError("fields", "premium", "Enter a valid amount (e.g., 500 or 500.50).");
      ok = false;
    } else {
      clearFieldError("fields", "premium");
    }

    // Payment mode (optional but if present must be from set)
    if (paymentMode && !allowedPaymentModes.has(paymentMode)) {
      setFieldError("fields", "paymentMode", "Choose Monthly, Quarterly, or Yearly.");
      ok = false;
    } else {
      clearFieldError("fields", "paymentMode");
    }

    // Dates (if provided, must be valid and in order)
    if (startDate && isNaN(new Date(startDate))) {
      setFieldError("fields", "startDate", "Enter a valid start date.");
      ok = false;
    } else {
      clearFieldError("fields", "startDate");
    }

    if (maturityDate && isNaN(new Date(maturityDate))) {
      setFieldError("fields", "maturityDate", "Enter a valid maturity date.");
      ok = false;
    } else {
      clearFieldError("fields", "maturityDate");
    }

    if (startDate && maturityDate) {
      const s = new Date(startDate);
      const m = new Date(maturityDate);
      if (!isNaN(s) && !isNaN(m) && m <= s) {
        setFieldError("fields", "maturityDate", "Maturity must be after start date.");
        ok = false;
      }
    }

    // Company / insuranceType / policyType (optional in your UI, but if user changed them, ensure non-empty)
    if (company === "") {
      // Only mark if policyData differs from original or user typed/cleared something
      if (originalData && company !== (originalData.company ?? "")) {
        setFieldError("fields", "company", "Company is required.");
        ok = false;
      }
    } else {
      clearFieldError("fields", "company");
    }

    if (insuranceType === "") {
      if (originalData && insuranceType !== (originalData.insuranceType ?? "")) {
        setFieldError("fields", "insuranceType", "Select a category.");
        ok = false;
      }
    } else {
      clearFieldError("fields", "insuranceType");
    }

    if (policyType === "") {
      if (originalData && policyType !== (originalData.policyType ?? "")) {
        setFieldError("fields", "policyType", "Select a type.");
        ok = false;
      }
    } else {
      clearFieldError("fields", "policyType");
    }

    return ok;
  };

  const hanldeUpdate = async () => {
    // Build diff
    const updatedFields = {};
    for (const key in policyData) {
      if (policyData[key] !== originalData[key]) {
        updatedFields[key] = policyData[key];
      }
    }
    if (Object.keys(updatedFields).length === 0) {
      alert("No changes detected");
      return;
    }

    // Validate before submit
    if (!validateBeforeUpdate()) {
      alert("Please correct highlighted fields.");
      return;
    }

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/policies/update/${policyData._id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedFields),
        }
      );
      const data = await res.json();
      if (res.ok) {
        alert("✅ Policy updated successfully!");
        setPolicyData(data.updatedPolicy);
        setOriginalData(data.updatedPolicy);
      } else {
        alert("❌ Update failed: " + data.msg);
      }
    } catch (error) {
      console.error("Error updating policy:", error);
      alert("Server error while updating");
    }
  };

  // ===== Customer suggestions =====
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (customerQuery.length < 2) {
        setCustomerSuggestions([]);
        setCustomerShowSuggestions(false);
        return;
      }
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/search-customer/${user.id}/${customerQuery}`);
        const data = await res.json();
        setCustomerSuggestions(data);
        setCustomerShowSuggestions(data.length > 0);
      } catch (err) {
        console.error("Error fetching suggestions", err);
        setCustomerSuggestions([]);
        setCustomerShowSuggestions(false);
      }
    };
    if (user?.id) fetchSuggestions();
  }, [customerQuery, user?.id]);

  const handleCustomerSelect = (suggestion) => {
    setSearchData({
      customerName: suggestion.name,
      policyNumber: suggestion.policyNumber,
    });
    clearFieldError("search", "customerName");
    clearFieldError("search", "policyNumber");
    setCustomerShowSuggestions(false);
    setCustomerQuery("");
  };

  return (
    <div className="dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="main-content pa-bg p-4">
        <h3 className="fw-bold text-center pa-title">Policy Alterations</h3>
        <p className="text-center text-muted mb-4">Search and modify policy details</p>

        {/* Search Card */}
        <div className="card-soft p-4 mb-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="icon-circle"><i className="bi bi-search" /></span>
            <h5 className="m-0" style={{color:"var(--teal-700)"}}>Search Policy</h5>
          </div>

          <form
            className="row g-3"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <div className="col-md-4 ig" style={{ position: "relative" }}>
              <label className="form-label">Customer Name</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-person" /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter customer name"
                  value={searchData.customerName}
                  onChange={(e) =>{
                    setSearchData({ ...searchData, customerName: e.target.value });
                    setCustomerQuery(e.target.value);
                    if (!e.target.value || e.target.value.length >= 2) {
                      clearFieldError("search", "customerName");
                    }
                  }}
                  style={getInputErrorStyle("search", "customerName")}
                />
              </div>
              {errors.search.customerName && <div style={errorText}>{errors.search.customerName}</div>}

              {/* Dropdown for customer suggestions */}
              {customerShowSuggestions && customerSuggestions.length > 0 && (
                <ul
                  style={{
                    listStyle: "none",
                    margin: 0,
                    padding: "8px 0",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    background: "white",
                    position: "absolute",
                    top: "76px",
                    zIndex: 2000,
                    width: "100%",
                    maxHeight: "220px",
                    overflowY: "auto",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "14px",
                  }}
                >
                  {customerSuggestions.map((customerSuggestion) => (
                    <li
                      key={customerSuggestion._id}
                      style={{
                        padding: "10px 15px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f5f9ff")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                      onClick={() => handleCustomerSelect(customerSuggestion)}
                    >
                      <span style={{ fontWeight: "500", color: "#333" }}>
                        {customerSuggestion.name}
                      </span>{" "}
                      <span style={{ color: "#888", fontSize: "13px" }}>
                        - {customerSuggestion.policyNumber}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="col-md-4 ig">
              <label className="form-label">Policy Number</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-hash" /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter policy number"
                  value={searchData.policyNumber}
                  onChange={(e) =>{
                    setSearchData({ ...searchData, policyNumber: e.target.value });
                    if (!e.target.value || policyNoPattern.test(e.target.value.trim())) {
                      clearFieldError("search", "policyNumber");
                    }
                    setCustomerQuery(e.target.value);
                  }}
                  style={getInputErrorStyle("search", "policyNumber")}
                />
              </div>
              {errors.search.policyNumber && <div style={errorText}>{errors.search.policyNumber}</div>}
            </div>

            <div className="col-md-4 d-flex align-items-end">
              <button type="submit" className="btn btn-teal w-100">
                <i className="bi bi-search me-2" />Search
              </button>
            </div>
          </form>
        </div>

        {/* Instructions */}
        {showInstructions && (
          <div className="help-banner p-4 mb-4 d-flex align-items-start gap-3">
            <div className="icon-circle" style={{width:36,height:36}}>
              <i className="bi bi-info-lg" />
            </div>
            <div>
              <h6 className="mb-1" style={{color:"var(--teal-700)"}}>Instructions</h6>
              <p className="mb-0 text-muted">
                Search for a policy using customer name and policy number. Once found, policy
                details will appear below. You can then update only the required fields.
              </p>
            </div>
          </div>
        )}

        {/* Details */}
        {policyData && (
          <>
            <div className="card-soft p-4 pa-form">
              <div className="row split-2">
                {/* LEFT COLUMN */}
                <div className="col-xl-6">
                  <div className="fieldset">
                    <div className="legend">
                      <i className="bi bi-person-lines-fill" />
                      Customer Information
                    </div>

                    <div className="ig mb-3">
                      <label className="form-label">Customer Name</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-person" /></span>
                        <input
                          className="form-control"
                          value={policyData.customerName}
                          name="customerName"
                          onChange={handleChange}
                          disabled
                        />
                      </div>
                      <div className="hint">This comes from the policy and can’t be edited here.</div>
                    </div>

                    <div className="ig mb-3">
                      <label className="form-label">Policy Number</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-hash" /></span>
                        <input
                          className="form-control"
                          value={policyData.policyNumber}
                          name="policyNumber"
                          onChange={handleChange}
                          disabled
                        />
                      </div>
                    </div>

                    <div className="ig mb-3">
                      <label className="form-label">Contact Number</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-telephone" /></span>
                        <input
                          className="form-control"
                          value={policyData.customerPhone || ""}
                          name="customerPhone"
                          onChange={handleChange}
                          placeholder="+91..."
                          style={getInputErrorStyle("fields", "customerPhone")}
                        />
                      </div>
                      {errors.fields.customerPhone && <div style={errorText}>{errors.fields.customerPhone}</div>}
                    </div>

                    <div className="ig mb-2">
                      <label className="form-label">Email</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-envelope" /></span>
                        <input
                          className="form-control"
                          value={policyData.customerEmail || ""}
                          name="customerEmail"
                          onChange={handleChange}
                          placeholder="name@example.com"
                          style={getInputErrorStyle("fields", "customerEmail")}
                        />
                      </div>
                      {errors.fields.customerEmail && <div style={errorText}>{errors.fields.customerEmail}</div>}
                    </div>
                  </div>

                  {/* PREMIUM */}
                  <div className="fieldset mt-4">
                    <div className="legend">
                      <i className="bi bi-cash-coin" />
                      Premium Information
                    </div>

                    <div className="ig mb-3">
                      <label className="form-label">Premium Amount (₹)</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-currency-rupee" /></span>
                        <input
                          className="form-control"
                          name="premium"
                          value={policyData.policyDetails?.premium || ""}
                          onChange={handleChange}
                          placeholder="Enter premium amount"
                          style={getInputErrorStyle("fields", "premium")}
                        />
                      </div>
                      {errors.fields.premium && <div style={errorText}>{errors.fields.premium}</div>}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Payment Mode</label>
                      <select
                        className="form-select"
                        name="paymentMode"
                        value={(policyData.policyDetails && policyData.policyDetails.paymentMode) || policyData.paymentMode || ""}
                        onChange={handleChange}
                        style={getInputErrorStyle("fields", "paymentMode")}
                      >
                        <option>Monthly</option>
                        <option>Quarterly</option>
                        <option>Yearly</option>
                      </select>
                      {errors.fields.paymentMode && <div style={errorText}>{errors.fields.paymentMode}</div>}
                    </div>

                    <div className="ig mb-3">
                      <label className="form-label">Premium Start Date</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-calendar-event" /></span>
                        <input
                          className="form-control"
                          type="date"
                          name="startDate"
                          value={policyData.policyDetails?.startDate || ""}
                          onChange={handleChange}
                          style={getInputErrorStyle("fields", "startDate")}
                        />
                      </div>
                      {errors.fields.startDate && <div style={errorText}>{errors.fields.startDate}</div>}
                    </div>

                    <div className="ig">
                      <label className="form-label">Maturity Date</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-calendar2-check" /></span>
                        <input
                          className="form-control"
                          type="date"
                          name="maturityDate"
                          value={policyData.policyDetails?.endDate || ""}
                          onChange={handleChange}
                          style={getInputErrorStyle("fields", "maturityDate")}
                        />
                      </div>
                      {errors.fields.maturityDate && <div style={errorText}>{errors.fields.maturityDate}</div>}
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="col-xl-6">
                  <div className="fieldset">
                    <div className="legend">
                      <i className="bi bi-shield-check" />
                      Policy Information
                    </div>

                    <div className="ig mb-3">
                      <label className="form-label">Insurance Company</label>
                      <div className="input-group">
                        <span className="input-group-text"><i className="bi bi-building" /></span>
                        <input
                          className="form-control"
                          value={policyData.company || ""}
                          name="company"
                          onChange={handleChange}
                          placeholder="Insurer name"
                          style={getInputErrorStyle("fields", "company")}
                        />
                      </div>
                      {errors.fields.company && <div style={errorText}>{errors.fields.company}</div>}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Insurance Category</label>
                      <select
                        className="form-select"
                        name="insuranceType"
                        value={policyData.insuranceType || ""}
                        onChange={handleChange}
                        style={getInputErrorStyle("fields", "insuranceType")}
                      >
                        <option>Life Insurance</option>
                        <option>General Insurance</option>
                      </select>
                      {errors.fields.insuranceType && <div style={errorText}>{errors.fields.insuranceType}</div>}
                      <div className="hint">Choose the broad category of the policy.</div>
                    </div>

                    <div>
                      <label className="form-label">Insurance Type</label>
                      <select
                        className="form-select"
                        name="policyType"
                        value={policyData.policyType || ""}
                        onChange={handleChange}
                        style={getInputErrorStyle("fields", "policyType")}
                      >
                        <option>Term</option>
                        <option>ULIP</option>
                        <option>Money Back</option>
                      </select>
                      {errors.fields.policyType && <div style={errorText}>{errors.fields.policyType}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit bar */}
              <div className="submit-bar d-flex justify-content-center mt-4">
                <button
                  type="button"
                  className="btn btn-teal px-4"
                  onClick={hanldeUpdate}
                  style={{ cursor: "pointer" }}
                >
                  <i className="bi bi-floppy me-2" />
                  Submit Alterations
                </button>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default PolicyAlterations;
